import React, { lazy } from "react";
import { Navigate, useParams } from "react-router";
import {
  isBespokeThreadsDomain,
  isDevDomain,
  isLuxtownDomain,
} from "../utils/index.js";
import { Require3pl } from "../helpers/Require3pl";
import HomeRedirect from "../pages/Dashboard/HomeRedirect";
import {
  SellersLegacyRedirect,
  SellerStockLegacyRedirect,
  WarehouseLegacyRedirect,
  SyncSettingsLegacyRedirect,
  BlankPosLegacyRedirect,
} from "../pages/Inventory/ThreePL/Redirects";
// Eager: a separate Blank Settings chunk 404s after deploys and every blank
// then dies on "A new version is ready". Heavy design-area/mockup editors stay
// lazy inside the page so fabric.js is not in the main bundle.
import BlankEditorRoute from "../pages/blankSettings/BlankEditorRoute";
// Eager shell: keeps VerticalLayout up and shows a page skeleton while
// the bulk-create chunk streams in. The page itself stays lazy inside.
import { withBatchNumberRoute } from "../components/batches/BatchNumberRoute";
import BulkProductCreator from "../pages/products/SelectedNewProducts/BulkProductCreator/BulkCreateRoute";

const BatchPickToPullRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/batches/${id}/pull`} replace />;
};

const HandheldFloorRedirect = () => <Navigate to="/handheld/floor" replace />;

const EcommerceAppPrivacyRedirect = () => (
  <Navigate to="/ecommerce-app/privacy" replace />
);

const gated3pl = (Component) => {
  const Wrapped = (props) => (
    <Require3pl>
      <Component {...props} />
    </Require3pl>
  );
  return Wrapped;
};
const InvoiceDetails = lazy(() => import("../pages/Invoices/InvoiceDetails"));
const PayoutStatement = lazy(() =>
  import("../pages/Payments/PayoutStatement")
);
const InvoicesOfUser = lazy(() => import("../pages/Invoices/InvoicesOfUser"));
const SellerDetail = lazy(() => import("../pages/Sellers/SellerDetail"));
// Legacy /order-charges now lives inside the platform Billing dashboard.
const OrderChargesRedirect = () => (
  <Navigate to="/settings/billing" replace />
);
const InvoicingUsers = lazy(() => import("../pages/Invoices/InvoicingUsers"));
const InvoicesOfBatch = lazy(() => import("../pages/Invoices/InvoicesOfBatch"));
const BatchesWithInvoices = lazy(() =>
  import("../pages/Invoices/BatchesWithInvoices")
);
const ResaleCertificates = lazy(() => import("../pages/ResaleCertificates"));
const SellerApplications = lazy(() => import("../pages/SellerApplications"));
const SellerApplicationDetail = lazy(() =>
  import("../pages/SellerApplications/ApplicationDetail")
);

const DraftOrders = lazy(() => import("../pages/Orders/DraftOrders"));
const UnsyncedOrders = lazy(() => import("../pages/Orders/UnsyncedOrders"));
const ShopifyFulfillmentFailures = lazy(() =>
  import("../pages/Orders/ShopifyFulfillmentFailures")
);
// // import { isDevDomain } from "../utils";

const Login = lazy(() => import("../pages/authentication/Login"));
const Onboarding = lazy(() => import("../pages/authentication/Onboarding"));
// New accounts.riverr.app wizard (Steps 2-5 of the founder signup flow).
// Step 1 is the Register page below, branched by domain to render
// `AccountsRegister`. These four live as separate routes (rather than
// query-param-driven) so URLs round-trip naturally and email-resume
// links can deep-link to a specific step.
const OnboardingBusiness = lazy(() =>
  import("../pages/onboarding/Business")
);
const OnboardingProduction = lazy(() =>
  import("../pages/onboarding/Production")
);
const OnboardingSales = lazy(() => import("../pages/onboarding/Sales"));
const OnboardingBranding = lazy(() =>
  import("../pages/onboarding/Branding")
);
const OnboardingPricing = lazy(() =>
  import("../pages/onboarding/Pricing")
);
const OnboardingProducts = lazy(() =>
  import("../pages/onboarding/Products")
);
const OnboardingPrintOptions = lazy(() =>
  import("../pages/onboarding/PrintOptions")
);
const OnboardingStore = lazy(() => import("../pages/onboarding/Store"));
const OnboardingComplete = lazy(() =>
  import("../pages/onboarding/Complete")
);
const ForgetPassword = lazy(() =>
  import("../pages/authentication/Login/ForgetPassword")
);
const NewStore = lazy(() => import("../pages/public/NewStore"));
const EmailVerification = lazy(() => import("../pages/EmailVerification"));
const Register = lazy(() => import("../pages/authentication/Register"));
const SellerSessionHandoff = lazy(() =>
  import("../pages/authentication/SellerSessionHandoff")
);
const PrivacyPage = lazy(() => import("../pages/public/Legal/PrivacyPage"));
const ShopifyPrivacyPage = lazy(() =>
  import("../pages/public/Legal/ShopifyPrivacyPage")
);
const TermsPage = lazy(() => import("../pages/public/Legal/TermsPage"));
const BillingInfoPage = lazy(() =>
  import("../pages/public/Legal/BillingInfoPage")
);
const Dashboard = lazy(() => import("../pages/Dashboard"));

const AnalyticsScreen = lazy(() =>
  import("../pages/Dashboard/AnalyticsScreen")
);
const AnalyticsQuote = lazy(() => import("../pages/Dashboard/AnalyticsQuote"));
const StorefrontAnalytics = lazy(() =>
  import("../pages/Dashboard/StorefrontAnalytics")
);
const EmployeeActivity = lazy(() => import("../pages/EmployeeActivity"));
const BatchedBlanksDemand = lazy(() =>
  import("../pages/Dashboard/BatchedBlanksDemand")
);
const TaxNexus = lazy(() => import("../pages/Analytics/TaxNexus"));
const TaxSalesTax = lazy(() => import("../pages/Analytics/TaxSalesTax"));
const Opportunities = lazy(() => import("../pages/Opportunities"));
const Profile = lazy(() => import("../pages/Profile"));
const SellerApplication = lazy(() => import("../pages/sellerApplication"));
const SellerApplicationPreview = lazy(() =>
  import("../pages/sellerApplication/preview/SellerApplicationPreview")
);
// // const Wallet = lazy(() => import("../pages/Wallet"));
const Payments = lazy(() => import("../pages/Payments"));
const SettingsRouter = lazy(() => import("../pages/Settings/SettingsRouter"));
const Integrations = lazy(() => import("../pages/Integrations"));
const Shops = lazy(() => import("../pages/Shops"));
const AddShop = lazy(() => import("../pages/Shops/AddNewShop/index.js"));
const ShopSettings = lazy(() => import("../pages/Shops/ShopSettings/index.js"));
const CopyStore = lazy(() => import("../pages/Shops/CopyStore"));

const EcommerceChannels = lazy(() =>
  import("../pages/Shops/EcommerceChannels")
);
const ShopRedirect = lazy(() => import("../pages/Shops/Redirects"));

const AllAddedBlanks = lazy(() =>
  import("../pages/LocalInventory/AllAddedBlanks")
);
const CatalogTiers = lazy(() => import("../pages/catalogTiers"));
const Listings = lazy(() => import("../pages/listings/Listings"));

const ListingDetails = lazy(() => import("../pages/listings/ListingDetails"));
const ItemsIndex = lazy(() => import("../pages/items/ItemsIndex"));
const Batches = lazy(() => import("../pages/batches/Batches"));
const TicketsRail = lazy(() => import("../pages/tickets"));
const MatchLabels = lazy(() => import("../pages/batches/MatchLabels"));
const NeedsLabels = lazy(() => import("../pages/production/NeedsLabels"));
const BatchDetais = withBatchNumberRoute(
  lazy(() => import("../pages/batches/BatchDetails")),
  { missPath: (value) => `/batch-item/${encodeURIComponent(value)}` }
);
const RoutedBatchDetails = lazy(() =>
  import("../pages/batches/RoutedBatchDetails")
);
const BatchPickMode = lazy(() => import("../pages/batches/BatchPickMode"));
// pick redirect kept for bookmarks; pull is the floor flow (PULL.1)
const BatchPullMode = withBatchNumberRoute(
  lazy(() => import("../pages/batches/BatchPullMode"))
);
const BatchDistributeMode = withBatchNumberRoute(
  lazy(() => import("../pages/batches/BatchDistributeMode"))
);
const BatchStagingMode = withBatchNumberRoute(
  lazy(() => import("../pages/batches/BatchStagingMode"))
);
const HandheldPutaway = lazy(() =>
  import("../pages/Inventory/HandheldPutaway")
);
const HandheldFloor = lazy(() => import("../pages/HandheldFloor"));
const HandheldWelcome = lazy(() => import("../pages/HandheldWelcome"));
const HandheldSettings = lazy(() => import("../pages/HandheldSettings/SettingsScreen"));
const HandheldScannerSetup = lazy(() => import("../pages/HandheldSettings/ScannerSetup"));
const HandheldRack = lazy(() => import("../pages/HandheldRack"));
const HandheldCollect = lazy(() => import("../pages/HandheldCollect"));
const HandheldShipping = lazy(() => import("../pages/HandheldShipping"));
const BatcheItemDetails = lazy(() =>
  import("../pages/batches/BatchItemDetails")
);
const EmbroideryStation = lazy(() =>
  import("../pages/EmbroideryStation")
);
const QcStation = lazy(() => import("../pages/QcStation"));
const NeckLabelStation = lazy(() => import("../pages/NeckLabelStation"));
const PackedStation = lazy(() => import("../pages/PackedStation"));
const DtfStation = lazy(() => import("../pages/DtfStation"));
const DtfCounts = lazy(() => import("../pages/DtfStation/DtfCounts"));
const EmbroideryCompletePreview = lazy(() =>
  import("../pages/EmbroideryStation/EmbroideryCompletePreview")
);
// Legacy `/purchase-orders` list page kept on disk as an anchor while the
// new Receiving page beds in; route now redirects to Receiving so existing
// bookmarks / internal navigate() calls land in the right place.
// const Orders = lazy(() => import("../pages/Orders"));
const MyOrders = lazy(() => import("../pages/Orders/AllOrders"));

// ── Customers ─────────────────────────────────────────────────────────────────
const CustomerList = lazy(() => import("../pages/Customers/CustomerList"));
const CustomerProfile = lazy(() =>
  import("../pages/Customers/CustomerProfile")
);

// ── Quotes & Sales Orders ─────────────────────────────────────────────────────
const QuoteList = lazy(() => import("../pages/Quotes/QuoteList"));
const QuoteBuilder = lazy(() => import("../pages/Quotes/QuoteBuilder"));
const RequestQuote = lazy(() => import("../pages/RequestQuote"));
const QuoteRequests = lazy(() => import("../pages/QuoteRequests"));
const QuoteRequestDetail = lazy(() =>
  import("../pages/QuoteRequests/QuoteRequestDetail")
);
const ClientQuote = lazy(() => import("../pages/Quotes/ClientQuote"));
const ClientQuoteChanges = lazy(() =>
  import("../pages/Quotes/ClientQuoteChanges")
);
const Inbox = lazy(() => import("../pages/Inbox"));
const SalesOrderList = lazy(() => import("../pages/SalesOrders/SalesOrderList"));
const SalesOrderDetail = lazy(() =>
  import("../pages/SalesOrders/SalesOrderDetail")
);
const ProductionCalendar = lazy(() => import("../pages/ProductionCalendar"));

// ── POS.1 Counter Register ────────────────────────────────────────────────────
const PosApp = lazy(() => import("../pages/Pos"));
const PosArtUploadPublic = lazy(() => import("../pages/Pos/ArtUploadPublic"));
const PosPayPublic = lazy(() => import("../pages/Pos/PosPayPublic"));
const PosArtReviewPublic = lazy(() => import("../pages/Pos/ArtReviewPublic"));
const PosQuoteList = lazy(() => import("../pages/Pos/PosQuoteList"));
const PosQuoteBuilder = lazy(() => import("../pages/Pos/PosQuoteBuilder"));

const InventorySyncSettings = lazy(() =>
  import("../pages/InventorySyncSettings")
);
const InventoryPools = lazy(() => import("../pages/InventoryPools"));
const InventoryPositions = lazy(() => import("../pages/InventoryPositions"));
const AdminStockSellers = lazy(() => import("../pages/AdminStockSellers"));
const AdminStockSellerDetail = lazy(() =>
  import("../pages/AdminStockSellerDetail")
);

const NewPurchaseOrder = lazy(() =>
  import("../pages/purchaseOrders/NewPurchaseOrder")
);
const NewPurchaseOrderDetail = lazy(() =>
  import("../pages/purchaseOrders/PurchaseOrders/PurchaseOrderDetails")
);
const ReceivePackage = lazy(() =>
  import("../pages/purchaseOrders/Receive")
);
const OrderDetails = lazy(() => import("../pages/Orders/OrderDetails"));

const OrderItemDetails = lazy(() => import("../pages/Orders/OrderItemDetails"));
const DraftOrderDetails = lazy(() =>
  import("../pages/Orders/DraftOrders/DraftOrderDetails")
);
const AdminDraftOrders = lazy(() => import("../pages/Orders/AdminDraftOrders"));
const AdminDraftOrderDetails = lazy(() =>
  import("../pages/Orders/AdminDraftOrders/AdminDraftOrderDetails")
);
const Products = lazy(() => import("../pages/products/Products"));

const ProductDetails = lazy(() => import("../pages/products/ProductDetails"));

const NewProduct = lazy(() => import("../pages/products/NewProduct"));

const NewProductIndex = lazy(() =>
  import("../pages/products/SelectedNewProducts/NewProductIndex")
);

const BlankProductDetails = lazy(() =>
  import("../pages/products/SelectedNewProducts/BlankProductDetails")
);
const NewProductDesign = lazy(() =>
  import("../pages/products/SelectedNewProducts/NewProductDesign")
);
const NewNecklaceDesign = lazy(() =>
  import("../pages/products/SelectedNewProducts/NewNecklaceDesign")
);
// // const NewStickerDesign = lazy(() =>
// //   import("../pages/products/SelectedNewProducts/NewStickerDesign")
// // );
// PRINT.1 — generic PrintSpec designer for single layout print products.
// Each gate reads `printProducts.unifiedDesigner` and falls back to the old
// editor (NewDieCutStickerDesign / pages/Poster, both still importable and
// lazily loaded inside pages/PrintProduct/routeGates) when the flag is off.
const PrintStickerRoute = lazy(() =>
  import("../pages/PrintProduct/routeGates").then((m) => ({ default: m.StickerRoute }))
);
const PrintRollLabelRoute = lazy(() =>
  import("../pages/PrintProduct/routeGates").then((m) => ({ default: m.RollLabelRoute }))
);
const PrintPosterRoute = lazy(() =>
  import("../pages/PrintProduct/routeGates").then((m) => ({ default: m.PosterRoute }))
);
const PrintPostcardRoute = lazy(() =>
  import("../pages/PrintProduct/routeGates").then((m) => ({ default: m.PostcardRoute }))
);
const EditProductGate = lazy(() =>
  import("../pages/products/SelectedNewProducts/EditProductGate")
);
// // const NewProductDesignOld = lazy(() =>
// //   import("../pages/products/SelectedNewProducts/NewProductDesignOld")
// // );
const CsvUpload = lazy(() => import("../pages/products/CsvUpload"));
const LocalInventoryProducts = lazy(() =>
  import("../pages/LocalInventory/Products")
);
// // const AddProductsForm = lazy(() =>
// //   import("../pages/LocalInventory/Products/AddProductsForm")
// // );
const NewManualProduct = lazy(() =>
  import("../pages/LocalInventory/NewManualProduct")
);
const LocalInventoryVariants = lazy(() =>
  import("../pages/LocalInventory/Variants")
);

// New unified inventory pages (directive: "Frontend Directive: Inventory UI").
// `/admin/inventory/stock` is the admin's primary surface for both blank
// inventory and seller-owned stock; `/admin/inventory/receiving` is the
// count desk (house POs pass through to PO confirm); `/inventory` is the
// seller's read-only view.
//
// Note the capital `I` in `Inventory` — this directory already exists for
// the legacy InventoryProducts/etc files, and webpack's resolver enforces
// case-sensitivity even on macOS's case-insensitive filesystem.
const AdminInventoryStock = lazy(() =>
  import("../pages/Inventory/InventoryStock")
);
const AdminInventoryActivity = lazy(() =>
  import("../pages/Inventory/Activity")
);
const AdminInventoryReceiving = lazy(() =>
  import("../pages/Inventory/InventoryReceiving")
);
const AdminBlankPurchaseOrders = lazy(() =>
  import("../pages/Inventory/BlankPurchaseOrders")
);
const AdminInventorySyncSettings = lazy(() =>
  import("../pages/Inventory/SyncSettings")
);
const AdminWarehouseLocations = lazy(() =>
  import("../pages/Inventory/WarehouseLocations")
);
const InventoryTasksPage = lazy(() =>
  import("../pages/Inventory/WarehouseLocations/InventoryTasksPage")
);
const ThreePLEnrollment = lazy(() =>
  import("../pages/Inventory/ThreePL/EnrollmentPage")
);
const AdminRetailStock = lazy(() =>
  import("../pages/Inventory/RetailStock")
);
const ThreePLSellers = lazy(() =>
  import("../pages/Inventory/ThreePL/SellersPage")
);
const ThreePLSellerDetail = lazy(() =>
  import("../pages/Inventory/ThreePL/SellerDetailPage")
);
// // const InventoryProductDetail = lazy(() =>
// //   import("../pages/Inventory/InventoryProductDetails")
// // );
// // const InventoryCustomers = lazy(() =>
// //   import("../pages/Inventory/InventoryCustomers")
// // );
// // const InventoryCart = lazy(() => import("../pages/Inventory/InventoryCart"));
// // const InventoryCheckout = lazy(() =>
// //   import("../pages/Inventory/InventoryCheckout")
// // );
// // const InventoryShops = lazy(() => import("../pages/Inventory/InventoryShops"));
const InventoryVariantEdit = lazy(() =>
  import("../pages/Inventory/VariantEdit")
);
const InventoryAddVariant = lazy(() =>
  import("../pages/Inventory/InventoryAddVariant")
);
const InventoryAddPrintedProduct = lazy(() =>
  import("../pages/Inventory/InventoryAddPrintedProduct")
);
const InventoryAddProduct = lazy(() =>
  import("../pages/Inventory/InventoryAddProduct")
);

const ManualOrders = lazy(() => import("../pages/ManualOrders"));
const ManualOrdersNew = lazy(() => import("../pages/ManualOrders-new"));

const Upgrade = lazy(() => import("../pages/Upgrade"));
const PricingContainer = lazy(() => import("../pages/PricingContainer"));
const SellerSubscriptionPage = lazy(() =>
  import("../pages/sellerSubscriptions/SellerSubscriptionPage")
);
// PRC.2.1 pricing tables editor — gated internally by pricingV3.enabled
const PricingContainerV3 = lazy(() => import("../pages/PricingContainerV3"));
// // const ProductEditor = lazy(() => import("../pages/products/ProductEditor"));

// //BINS
const Bins = lazy(() => import("../pages/Bins"));
const ShippingStation = lazy(() => import("../pages/Bins/ShippingStation"));
const BinDetails = lazy(() => import("../pages/Bins/BinDetails"));

const Shipments = lazy(() => import("../pages/Shipments"));
const Addresses = lazy(() => import("../pages/Addresses"));

const ShopifyAppEditor = lazy(() => import("../pages/ShopifyAppEditor"));

// //Tiers page
const Tiers = lazy(() => import("../pages/tiers/index"));

const BatchByItems = lazy(() => import("../pages/items/BatchByItems"));
const BatchByOrders = lazy(() => import("../pages/items/BatchByOrders"));
const BatchSchedulers = lazy(() => import("../pages/batches/BatchSchedulers"));
const ShippingSpeed = lazy(() => import("../pages/ShippingSpeed/index.js"));
const PopupStoresShippingSpeed = lazy(() =>
  import("../pages/PopupStoresShippingOptions")
);
const ShippingPrice = lazy(() => import("../pages/ShippingPrice"));
const Category = lazy(() => import("../pages/Category"));
const Locations = lazy(() => import("../pages/Locations"));
const Members = lazy(() => import("../pages/Members"));
const EnterpriseBranding = lazy(() => import("../pages/EnterpriseBranding"));
const Enterprises = lazy(() => import("../pages/Enterprises"));

// // const Asana = lazy(() => import("../pages/HelpDesk/Asana"));
const AQCItems = lazy(() => import("../pages/batches/AQCItems"));
const QualityControlFailed = lazy(() =>
  import("../pages/batches/QualtiControlFailed")
);
const ShareAccess = lazy(() => import("../pages/ShareAccess"));
const LateOrders = lazy(() => import("../pages/Orders/LateOrders"));
const SplitShipPending = lazy(() => import("../pages/Orders/SplitShipPending"));
const ImageBins = lazy(() => import("../pages/ImageBins"));
const Catalog = lazy(() => import("../pages/products/Catalog"));
const Nester = lazy(() => import("../pages/Nester"));
const DtfStationTransfers = lazy(() =>
  import("../pages/DtfStation/DtfStationTransfers")
);
const DtfStationGangsheet = lazy(() =>
  import("../pages/DtfStation/DtfStationGangsheet")
);
const DtfSavedSheets = lazy(() =>
  import("../pages/DtfStation/DtfSavedSheets")
);
const StickerSheet = lazy(() => import("../pages/StickerSheet"));
const EmbroideryOptions = lazy(() => import("../pages/EmbroideryOptions"));
const EnterpriseDomains = lazy(() => import("../pages/EnterpriseDomains"));
const BespokeAdminPanelEnterprises = lazy(() =>
  import("../pages/BespokeAdminPanel/Enterprises")
);
const BespokeAdminPanelUsers = lazy(() =>
  import("../pages/BespokeAdminPanel/Users")
);
const BlankProducts = lazy(() =>
  import("../pages/blankProducts/TypeOfProduct")
);
const SupplierBlankProduct = lazy(() =>
  import("../pages/blankProducts/SupplierBlankProduct")
);
const ManualBlankProduct = lazy(() =>
  import("../pages/blankProducts/ManualBlankProduct")
);
const RiverrBlankProduct = lazy(() =>
  import("../pages/blankProducts/RiverrBlankProduct")
);
const CatalogBlankProduct = lazy(() =>
  import("../pages/blankProducts/CatalogBlankProduct")
);
const PopupStorePayouts = lazy(() => import("../pages/PopupStorePayouts"));
const PartnerPayouts = lazy(() => import("../pages/PartnerPayouts"));
const PartnerStatementDocument = lazy(() =>
  import("../pages/PartnerPayouts/StatementDocument"),
);
const PSDLibraryUpload = lazy(() => import("../pages/BulkPsdUploader"));
const PSDLibrary = lazy(() => import("../pages/PSDLibrary"));
const DesignLibrary = lazy(() => import("../pages/DesignLibrary"));
const DesignStudio = lazy(() => import("../pages/DesignLibrary/Studio"));
const DstLibraryRedirect = () => (
  <Navigate to="/design-library" replace state={{ tab: "dst" }} />
);
const publicPsds = lazy(() => import("../pages/PublicPsds"));

const ApiKeys = lazy(() => import("../pages/ApiKeys"));
// KOR.1 §4 — operator machine screen (kiosk browser above the Kornit; no chrome)
const MachineScreen = lazy(() => import("../pages/MachineScreen"));
const MachinePicker = lazy(() => import("../pages/MachineScreen/MachinePicker"));

const authProtectedRoutes = [
  { path: "/dashboard", exact: true, component: Dashboard },
  { path: "/handheld", exact: true, component: HandheldWelcome },
  { path: "/handheld/settings", exact: true, component: HandheldSettings },
  { path: "/handheld/settings/scanner", exact: true, component: HandheldScannerSetup },
  { path: "/handheld/rack", exact: true, component: HandheldRack },
  { path: "/handheld/collect", exact: true, component: HandheldCollect },
  { path: "/handheld/shipping", exact: true, component: HandheldShipping },
  { path: "/handheld/floor", exact: true, component: HandheldFloor },
  { path: "/handheld/sort-bins", exact: true, component: HandheldFloorRedirect },
  { path: "/analytics-screen", exact: true, component: AnalyticsScreen },
  {
    path: "/analytics/storefront",
    exact: true,
    component: StorefrontAnalytics,
  },
  { path: "/analytics/quotes/:quoteId", exact: true, component: AnalyticsQuote },
  { path: "/tax/nexus", exact: true, component: TaxNexus },
  { path: "/tax/sales-tax", exact: true, component: TaxSalesTax },
  {
    path: "/analytics/tax/nexus",
    exact: true,
    component: () => <Navigate to="/tax/nexus" replace />,
  },
  {
    path: "/analytics/tax/sales-tax",
    exact: true,
    component: () => <Navigate to="/tax/sales-tax" replace />,
  },
  { path: "/employee-activity", exact: true, component: EmployeeActivity },
  {
    path: "/analytics/batched-blanks-demand",
    exact: true,
    component: BatchedBlanksDemand,
  },
  { path: "/opportunities", exact: true, component: Opportunities },
  { path: "/public-psds", exact: true, component: publicPsds },

  { path: "/api-keys", exact: true, component: ApiKeys },
  // KOR.1 §4 — pick a station, then kiosk bound by URL.
  {
    path: "/machines",
    component: MachinePicker,
    exact: true,
    standalone: true,
  },
  {
    path: "/machines/:machineId",
    component: MachineScreen,
    exact: true,
    standalone: true,
  },
  // Seller onboarding application — signed-in sellers only, full-screen
  // (no dashboard chrome) so it works as a phone-first wizard and so the
  // locked-access gate in VerticalLayout can't loop on it.
  { path: "/apply", component: SellerApplication, exact: true, standalone: true },
  {
    path: "/apply/:stepId",
    component: SellerApplication,
    exact: true,
    standalone: true,
  },
  // Admin "Preview as a seller" (Settings → Seller onboarding).
  {
    path: "/seller-onboarding/preview",
    component: SellerApplicationPreview,
    exact: true,
    standalone: true,
  },
  {
    path: "/seller-onboarding/preview/:stepId",
    component: SellerApplicationPreview,
    exact: true,
    standalone: true,
  },
  { path: "/profile", component: Profile },
  { path: "/wallet", component: Payments },
  // // { path: "/payments", component: Payments },
  { path: "/integrations", component: Integrations, exact: true },
  { path: "/ecommerce-channels", component: EcommerceChannels, exact: true },
  { path: "/shops", component: Shops, exact: true },
  { path: "/shops/:shopId/copy", component: CopyStore, exact: true },
  { path: "/add-shop", component: AddShop, exact: true },
  { path: "/stores/:shopId/*", component: ShopSettings },
  { path: "/shop/settings/:shopId/*", component: ShopSettings },
  { path: "/shop/settings", component: ShopSettings, exact: true },

  {
    path: "/shops/redirects/:platformName",
    component: ShopRedirect,
    exact: true,
  },
  { path: "/settings/*", component: SettingsRouter },
  // NAV.1: admin Base products. Sellers keep legacy /all-added-blanks.
  { path: "/products/blanks", component: AllAddedBlanks, exact: true },
  {
    // Routes through BlankEditorRoute so the `blankEditor.unified` flag
    // can pick V2 (unified shell) over V1 (tabbed editor). V1 is lazy
    // loaded by the adapter when the flag is off.
    path: "/products/blanks/:blankId/*",
    component: BlankEditorRoute,
  },
  { path: "/all-added-blanks", component: AllAddedBlanks, exact: true },
  { path: "/catalog-tiers", component: CatalogTiers, exact: true },
  { path: "/listings", component: Listings, exact: true },

  { path: "/listings/:id", component: ListingDetails, exact: true },
  { path: "/items", component: ItemsIndex, exact: true },
  { path: "/batches", component: Batches, exact: true },
  // TIX.1 — full-screen manufacturing board (no admin chrome)
  {
    path: "/tickets",
    component: TicketsRail,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/tickets/floor",
    component: () => <Navigate to="/tickets" replace />,
    exact: true,
  },
  { path: "/batches/match", component: MatchLabels, exact: true },
  // ERP lines waiting on a product mapping (admin only on the server).
  { path: "/production/needs-labels", component: NeedsLabels, exact: true },
  {
    path: "/batches/routed/:id",
    component: RoutedBatchDetails,
    exact: true,
  },
  { path: "/batches/:id", component: BatchDetais, exact: true },
  { path: "/batches/:id/pull", component: BatchPullMode, exact: true },
  { path: "/batches/:id/distribute", component: BatchDistributeMode, exact: true },
  {
    path: "/batches/:id/pick",
    component: BatchPickToPullRedirect,
    exact: true,
  },
  { path: "/batches/:id/stage", component: BatchStagingMode, exact: true },
  { path: "/batches/:id/staging", component: BatchStagingMode, exact: true },
  { path: "/batch-item/:id", component: BatcheItemDetails, exact: true },
  { path: "/qc-station", component: QcStation, exact: true, standalone: true, withAdminProviders: true },
  {
    path: "/neck-label-station",
    component: NeckLabelStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  { path: "/packed-station", component: PackedStation, exact: true, standalone: true, withAdminProviders: true },
  {
    path: "/qc-station/:id",
    component: QcStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/qc-station/orders/:id",
    component: QcStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/neck-label-station/:id",
    component: NeckLabelStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/neck-label-station/orders/:id",
    component: NeckLabelStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/embroidery-station",
    component: EmbroideryStation,
    exact: true,
    standalone: true,
  },
  {
    path: "/embroidery-station/:id",
    component: EmbroideryStation,
    exact: true,
    standalone: true,
  },
  { path: "/dtf-station", component: DtfStation, exact: true, standalone: true },
  {
    path: "/dtf-station/counts",
    component: DtfCounts,
    exact: true,
    standalone: true,
  },
  {
    path: "/dtf-station/transfers",
    component: DtfStationTransfers,
    exact: true,
    standalone: true,
  },
  {
    path: "/dtf-station/gangsheet",
    component: DtfStationGangsheet,
    exact: true,
    standalone: true,
  },
  {
    path: "/dtf-station/saved-sheets",
    component: DtfSavedSheets,
    exact: true,
    standalone: true,
  },
  {
    path: "/dtf-station/:id",
    component: DtfStation,
    exact: true,
    standalone: true,
  },
  {
    path: "/admin/inventory/putaway/handheld",
    component: HandheldPutaway,
    exact: true,
  },
  {
    path: "/purchase-orders",
    exact: true,
    component: () => (
      <Navigate to="/admin/inventory/purchase-orders" replace />
    ),
  },
  { path: "/orders", component: MyOrders, exact: true },
  // // { path: "/orders-new", component: MyOrders, exact: true },

  // ── Customers ─────────────────────────────────────────────────────────────
  { path: "/customers", component: CustomerList, exact: true },
  { path: "/customers/:id", component: CustomerProfile, exact: true },

  // ── Quotes & Sales Orders ─────────────────────────────────────────────────
  { path: "/quotes", component: QuoteList, exact: true },
  { path: "/quotes/new", component: QuoteBuilder, exact: true },
  { path: "/quotes/:id", component: QuoteBuilder, exact: true },
  // Seller bulk / custom quote requests (not the admin QuoteBuilder).
  { path: "/request-quote", component: RequestQuote, exact: true },
  { path: "/quote-requests", component: QuoteRequests, exact: true },
  { path: "/quote-requests/:id", component: QuoteRequestDetail, exact: true },
  // Customer messaging inbox (email-backed CRM threads).
  { path: "/inbox", component: Inbox, exact: true },
  { path: "/inbox/:conversationId", component: Inbox, exact: true },
  { path: "/sales-orders", component: SalesOrderList, exact: true },
  { path: "/sales-orders/:id", component: SalesOrderDetail, exact: true },
  { path: "/production-calendar", component: ProductionCalendar, exact: true },

  // Full admin Quotes UI hosted inside POS (Apollo/Polaris, no VerticalLayout)
  // Still auth protected — Register staff use /pos till only.
  {
    path: "/pos/quotes",
    component: PosQuoteList,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/pos/quotes/new",
    component: PosQuoteBuilder,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/pos/quotes/:id",
    component: PosQuoteBuilder,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },

  
  // Inventory / Admin Stock
  {
    path: "/inventory-sync-settings",
    component: InventorySyncSettings,
    exact: true,
  },
  {
    path: "/inventory-pools",
    component: InventoryPools,
    exact: true,
  },
  {
    path: "/inventory-positions",
    component: InventoryPositions,
    exact: true,
  },
  {
    path: "/admin/inventory/positions",
    component: InventoryPositions,
    exact: true,
  },
  {
    path: "/admin/inventory/sellers",
    component: SellersLegacyRedirect,
    exact: true,
  },
  {
    path: "/admin/inventory/sellers/:enterpriseId",
    component: AdminStockSellerDetail,
    exact: true,
  },
  
  { path: "/manual-orders", component: ManualOrders, exact: true },
  { path: "/manual-orders-new", component: ManualOrdersNew, exact: true },

  { path: "/pricing", component: Upgrade, exact: true },
  { path: "/pricing-table", component: PricingContainer, exact: true },
  { path: "/subscription", component: SellerSubscriptionPage, exact: true },
  { path: "/pricing-tables-v3", component: PricingContainerV3, exact: true },
  { path: "/orders/:id", component: OrderDetails, exact: true },

  {
    path: "/orders/:orderId/items/:id",
    component: OrderItemDetails,
    exact: true,
  },
  { path: "/new-purchase-order", component: NewPurchaseOrder },
  {
    path: "/purchase-order/:id/receive",
    component: ReceivePackage,
    exact: true,
  },
  { path: "/purchase-order/:id", component: NewPurchaseOrderDetail },

  { path: "/draft-orders", component: DraftOrders, exact: true },
  { path: "/draft-orders/:id", component: DraftOrderDetails, exact: true },
  { path: "/admin-draft-orders", component: AdminDraftOrders, exact: true },
  {
    path: "/admin-draft-orders/:id",
    component: AdminDraftOrderDetails,
    exact: true,
  },
  { path: "/unsynced-orders", component: UnsyncedOrders, exact: true },
  {
    path: "/shopify-send-failures",
    component: ShopifyFulfillmentFailures,
    exact: true,
  },
  { path: "/late-orders", component: LateOrders, exact: true },
  { path: "/split-ship-pending", component: SplitShipPending, exact: true },
  { path: "/products", component: Products, exact: true },
  { path: "/products/new", component: NewProduct, exact: true },
  { path: "/products/new-product", component: NewProductIndex, exact: true },
  {
    path: "/products/bulk-create",
    component: BulkProductCreator,
    exact: true,
  },
  {
    path: "/products/bulk-create/:legacyStep",
    component: BulkProductCreator,
    exact: true,
  },
  {
    path: "/products/choose-products",
    component: BulkProductCreator,
    exact: true,
  },

  {
    path: "/products/new-product/:id",
    component: NewProductDesign,
    exact: true,
    // Full-screen customizer: render without the admin chrome/providers
    // (Apollo/Polaris). See App.js route rendering + providers/AdminProviders.
    standalone: true,
  },
  {
    path: "/products/new-product/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/new-necklace/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/sticker/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/new-sticker/:id/details",
    component: BlankProductDetails,
    exact: true,
  },

  {
    path: "/products/new-necklace/:id",
    component: NewNecklaceDesign,
    exact: true,
    standalone: true,
  },
  {
    path: "/products/sticker/:id",
    // PRINT.1: PrintProduct when printProducts.unifiedDesigner is on, else
    // NewDieCutStickerDesign (see pages/PrintProduct/routeGates).
    component: PrintStickerRoute,
    exact: true,
    // Full-screen sticker editor (`.app { position: fixed; inset: 0 }`) already
    // covers the admin chrome, so rendering standalone is a visual no-op that
    // also drops the Apollo/Polaris the chrome used to pull in.
    standalone: true,
  },
  // // {
  // //   path: "/products/new-sticker/:id",
  // //   component: NewStickerDesign,
  // //   exact: true,
  // // },
  {
    path: "/products/sticker-sheet/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/sticker-sheet/:id",
    component: StickerSheet,
    exact: true,
    standalone: true,
  },
  {
    path: "/products/roll-label/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/roll-label/:id",
    // PRINT.1: PrintProduct when printProducts.unifiedDesigner is on, else
    // NewDieCutStickerDesign (rollLabel forks live inside it).
    component: PrintRollLabelRoute,
    exact: true,
    standalone: true,
  },
  {
    path: "/products/postcard/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/postcard/:id",
    // PRINT.1: postcards only exist in the generic PrintProduct host.
    component: PrintPostcardRoute,
    exact: true,
    standalone: true,
  },
  {
    path: "/products/nester/:id",
    component: Nester,
    exact: true,
    standalone: true,
  },
  {
    path: "/products/poster/:id/details",
    component: BlankProductDetails,
    exact: true,
  },
  {
    path: "/products/poster/:id",
    // PRINT.1: PrintProduct when printProducts.unifiedDesigner is on, else Poster.
    component: PrintPosterRoute,
    exact: true,
    standalone: true,
  },
  {
    path: "/products/edit-product/:id/:productId",
    component: EditProductGate,
    exact: true,
    standalone: true,
  },
  // // { path: "/products/new/editor", component: ProductEditor, exact: true },
  { path: "/products/csv-upload", component: CsvUpload, exact: true },

  // { path: "/unsynced-orders", component: UnsyncedOrders, exact: true },
  // { path: "/unsynced-orders", component: UnsyncedOrders, exact: true },
  // ─── Unified inventory (directive: "Frontend Directive: Inventory UI") ───
  {
    path: "/admin/inventory/activity",
    component: AdminInventoryActivity,
    exact: true,
  },
  {
    path: "/admin/inventory/stock",
    component: AdminInventoryStock,
    exact: true,
  },
  {
    path: "/admin/inventory/retail-stock",
    component: SellerStockLegacyRedirect,
    exact: true,
  },
  {
    path: "/admin/inventory/receiving",
    component: AdminInventoryReceiving,
    exact: true,
  },
  {
    path: "/admin/inventory/blank-pos",
    component: BlankPosLegacyRedirect,
    exact: true,
  },
  {
    path: "/admin/inventory/purchase-orders",
    component: AdminBlankPurchaseOrders,
    exact: true,
  },
  {
    // Buy this week was replaced by Stock → Running low.
    path: "/admin/inventory/buy-this-week",
    component: () => <Navigate to="/admin/inventory/stock?panel=running-low" replace />,
    exact: true,
  },
  {
    path: "/admin/inventory/tasks",
    component: InventoryTasksPage,
    exact: true,
  },
  {
    path: "/admin/inventory/sync-settings",
    component: SyncSettingsLegacyRedirect,
    exact: true,
  },
  {
    path: "/admin/inventory/warehouse-locations",
    component: WarehouseLegacyRedirect,
    exact: true,
  },
  // INV.1 — 3PL (gated by inventory.3pl)
  {
    path: "/admin/3pl/receiving",
    component: () => <Navigate to="/admin/inventory/receiving" replace />,
    exact: true,
  },
  {
    path: "/admin/3pl/enrollment",
    component: gated3pl(ThreePLEnrollment),
    exact: true,
  },
  {
    path: "/admin/3pl/retail-stock",
    component: gated3pl(AdminRetailStock),
    exact: true,
  },
  {
    path: "/admin/3pl/seller-stock",
    component: SellerStockLegacyRedirect,
    exact: true,
  },
  {
    path: "/admin/3pl/sellers",
    component: gated3pl(ThreePLSellers),
    exact: true,
  },
  {
    path: "/admin/3pl/sellers/:sellerId",
    component: gated3pl(ThreePLSellerDetail),
    exact: true,
  },
  {
    path: "/admin/3pl/sync-settings",
    component: gated3pl(AdminInventorySyncSettings),
    exact: true,
  },
  // INV.5 — seller Send inventory (gated by admin inventory.3pl).
  // My stock · Send inventory · Shipments. Old pages redirect here.
  {
    path: "/inventory",
    component: gated3pl(
      lazy(() => import("../pages/Inventory/SellerInventory/MyStock"))
    ),
    exact: true,
  },
  {
    path: "/inventory/send",
    component: gated3pl(
      lazy(() => import("../pages/Inventory/SellerInventory/SendInventory"))
    ),
    exact: true,
  },
  {
    path: "/inventory/shipments",
    component: gated3pl(
      lazy(() => import("../pages/Inventory/SellerInventory/SellerShipments"))
    ),
    exact: true,
  },
  {
    path: "/inventory/shipments/:shipmentId",
    component: gated3pl(
      lazy(() => import("../pages/Inventory/SellerInventory/SellerShipments"))
    ),
    exact: true,
  },
  {
    // Products the fulfiller prints and keeps for the seller.
    path: "/inventory/print-for-me",
    component: gated3pl(
      lazy(() =>
        import("../pages/Inventory/SellerInventory/MyProducts").then((m) => ({
          default: (props) => <m.default {...props} printOnly />,
        }))
      )
    ),
    exact: true,
  },
  ...[
    "/inventory/enroll",
    "/inventory/add-product",
    "/inventory/send-stock",
  ].map((path) => ({
    path,
    component: gated3pl(() => <Navigate to="/inventory/send" replace />),
    exact: true,
  })),
  ...[
    "/inventory/my-products",
    "/inventory/my-inventory",
    "/inventory/overview",
  ].map((path) => ({
    path,
    component: gated3pl(() => <Navigate to="/inventory" replace />),
    exact: true,
  })),
  {
    path: "/inventory/variances",
    component: gated3pl(() => <Navigate to="/inventory/shipments" replace />),
    exact: true,
  },
  {
    path: "/inventory/sync-settings",
    component: gated3pl(AdminInventorySyncSettings),
    exact: true,
  },

  {
    path: "/inventory-products",
    component: LocalInventoryProducts,
    exact: true,
  },
  // // { path: "/inventory-products/new", component: AddProductsForm, exact: true },
  {
    path: "/inventory-products/new/manual-product",
    component: NewManualProduct,
    exact: true,
  },
  {
    path: "/inventory-products/:id",
    component: LocalInventoryVariants,
    exact: true,
  },
  {
    path: "/inventory-variants",
    component: LocalInventoryVariants,
    exact: true,
  },
  // // { path: "/inventory-product-detail", component: InventoryProductDetail },
  // { path: "/inventory-customers", component: InventoryCustomers },
  // { path: "/inventory-cart", component: InventoryCart },
  // { path: "/inventory-checkout", component: InventoryCheckout },
  // { path: "/inventory-shops", component: InventoryShops },
  { path: "/inventory-add-product", component: InventoryAddProduct },
  { path: "/inventory-variant-edit/:id", component: InventoryVariantEdit },
  { path: "/inventory-add-variant", component: InventoryAddVariant },
  { path: "/inventory-add-printed", component: InventoryAddPrintedProduct },
  // // { path: "/shopify-user", component: ShopifyUser, exact: true },

  // //bins
  { path: "/bins", component: Bins, exact: true },
  {
    path: "/bins/shipping",
    component: ShippingStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/bins/shipping/:id",
    component: ShippingStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  {
    path: "/bins/shipping/orders/:id",
    component: ShippingStation,
    exact: true,
    standalone: true,
    withAdminProviders: true,
  },
  { path: "/bins/:id", component: BinDetails, exact: true },

  // // INVOICES

  {
    path: "/users",
    component: InvoicingUsers,
    exact: true,
  },
  {
    path: "/users/:id/invoices",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id/shipping",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id/billing",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id/pricing",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id/ship-out",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id/shopify",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id/advanced-settings",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/users/:id",
    component: SellerDetail,
    exact: true,
  },
  {
    path: "/resale-certificates",
    component: ResaleCertificates,
    exact: true,
  },
  // Seller onboarding review queue (admins; the pages redirect others).
  {
    path: "/seller-applications",
    component: SellerApplications,
    exact: true,
  },
  {
    path: "/seller-applications/:uid",
    component: SellerApplicationDetail,
    exact: true,
  },
  {
    path: "/invoices",
    component: InvoicesOfBatch,
    exact: true,
  },
  {
    path: "/batch-invoices",
    component: BatchesWithInvoices,
    exact: true,
  },
  {
    path: "/order-charges",
    component: OrderChargesRedirect,
    exact: true,
  },
  {
    path: "/users/:id/invoices/:invoice_id",
    component: InvoiceDetails,
    exact: true,
  },
  {
    path: "/payout-statements/:orderId",
    component: PayoutStatement,
    exact: true,
  },

  { path: "/shipments", component: Shipments },

  { path: "/addresses", component: Addresses },
  { path: "/tiers", component: Tiers, expect: true },
  { path: "/batch-by-items", component: BatchByItems, expect: true },
  // // { path: "/help-desk", component: Asana, expect: true },
  { path: "/batch-by-orders", component: BatchByOrders, expect: true },
  { path: "/batch-schedulers", component: BatchSchedulers, expect: true },
  { path: "/shipping-settings", component: ShippingSpeed, exact: true },
  {
    path: "/shipping-options-popup-store",
    component: PopupStoresShippingSpeed,
    exact: true,
  },
  { path: "/shipping-zones", component: ShippingPrice, exact: true },
  { path: "/product-categories", component: Category, exact: true },
  { path: "/locations", component: Locations, exact: true },
  { path: "/members", component: Members, exact: true },
  { path: "/business-branding", component: EnterpriseBranding, exact: true },
  {
    path: "/order-email-template",
    exact: true,
    component: () => (
      <Navigate to="/settings/email/admin-alerts" replace />
    ),
  },
  { path: "/enterprises", component: Enterprises, exact: true },
  { path: "/aqc-items", component: AQCItems, exact: true },
  {
    path: "/quality-control-failed-items",
    component: QualityControlFailed,
    exact: true,
  },
  {
    path: "/share-access",
    component: ShareAccess,
    exact: true,
  },
  {
    path: "/image-bins",
    component: ImageBins,
    exact: true,
  },
  // NAV.1: Third Party Catalog → Marketplace (route live; nav entry hidden).
  {
    path: "/marketplace",
    component: Catalog,
    exact: true,
  },
  {
    path: "/products/third-party-catalog",
    exact: true,
    component: () => <Navigate to="/marketplace" replace />,
  },
  {
    path: "/nester",
    component: Nester,
    exact: true,
    standalone: true,
  },
  {
    path: "/dtf-transfers",
    exact: true,
    standalone: true,
    component: () => <Navigate to="/dtf-station/transfers" replace />,
  },
  {
    path: "/embroidery-options",
    component: EmbroideryOptions,
    exact: true,
  },
  {
    path: "/enterprise-domains",
    component: EnterpriseDomains,
    exact: true,
  },
  {
    path: "/bespoke-admin-panel",
    component: BespokeAdminPanelEnterprises,
    exact: true,
  },
  {
    path: "/bespoke-admin-panel/:id/users",
    component: BespokeAdminPanelUsers,
    exact: true,
  },
  {
    path: "/blank-products/new",
    component: BlankProducts,
    exact: true,
  },
  {
    path: "/blank-products/new/supplier-product",
    component: SupplierBlankProduct,
    exact: true,
  },
  {
    path: "/blank-products/new/catalog-product",
    component: CatalogBlankProduct,
    exact: true,
  },
  {
    path: "/blank-products/new/manual-product",
    component: ManualBlankProduct,
    exact: true,
  },
  {
    path: "/blank-products/new/riverr-product",
    component: RiverrBlankProduct,
    exact: true,
  },
  {
    path: "/blank-products/edit/riverr-product/:blankId",
    component: RiverrBlankProduct,
    exact: true,
  },
  {
    // Canonical franchisee payouts route, surfaced as the top-level
    // "Payouts" main-nav row (see helpers/mainNav.js).
    path: "/payouts",
    component: PopupStorePayouts,
    exact: true,
  },
  {
    // Legacy alias kept registered so existing deep links keep working
    // and the Stripe Connect onboarding return URL
    // (`CONNECT_RETURN_PATH = "/popup-store-payouts?connect=return"` in
    // riverr-backend-python/V2/functions/Stripe/main.py) still resolves.
    path: "/popup-store-payouts",
    component: PopupStorePayouts,
    exact: true,
  },
  {
    // PAY.1 — commission partner payouts (roster, ledger, runs, statements).
    path: "/partner-payouts",
    component: PartnerPayouts,
    exact: true,
  },
  {
    path: "/partner-payouts/statements/:statementId",
    component: PartnerStatementDocument,
    exact: true,
  },
  {
    path: "/psd-library/bulk-upload",
    component: PSDLibraryUpload,
    exact: true,
  },
  {
    path: "/psd-library",
    component: PSDLibrary,
    exact: true,
  },
  {
    path: "/design-library",
    component: DesignLibrary,
    exact: true,
  },
  {
    path: "/dst-library",
    component: DstLibraryRedirect,
    exact: true,
  },
  {
    path: "/design-library/studio",
    component: DesignStudio,
    exact: true,
    standalone: true,
  },
  {
    path: "/design-library/studio/:id",
    component: DesignStudio,
    exact: true,
    standalone: true,
  },
  {
    path: "/api-keys",
    component: ApiKeys,
    exact: true,
  },

  ...(process.env.NODE_ENV !== "production"
    ? [
        {
          path: "/dev/work-order-6-placements",
          component: lazy(() => import("../pages/DevWorkOrderSixPlacements")),
          exact: true,
          standalone: true,
        },
        {
          path: "/dev/shipping-error-preview",
          component: lazy(() => import("../pages/DevShippingErrorPreview")),
          exact: true,
        },
      ]
    : []),

  // this route should be at the end of all other routes
  { path: "/", exact: true, component: HomeRedirect },
];

const publicRoutes = [
  { path: "/onboarding", component: Onboarding, exact: true },
  // Wizard steps live on the public router (no `authProtectedRoutes`
  // wrapper) so we control the auth gate via `useOnboardingGuard`
  // instead — the user IS signed in by this point, but the wizard
  // pages must NOT render the VerticalLayout chrome (sidebar,
  // topbar) reserved for the dashboard.
  { path: "/onboarding/business", component: OnboardingBusiness, exact: true },
  {
    path: "/onboarding/production",
    component: OnboardingProduction,
    exact: true,
  },
  { path: "/onboarding/sales", component: OnboardingSales, exact: true },
  { path: "/onboarding/branding", component: OnboardingBranding, exact: true },
  { path: "/onboarding/pricing", component: OnboardingPricing, exact: true },
  { path: "/onboarding/products", component: OnboardingProducts, exact: true },
  {
    path: "/onboarding/print-options",
    component: OnboardingPrintOptions,
    exact: true,
  },
  { path: "/onboarding/store", component: OnboardingStore, exact: true },
  // Post-finalize handoff interstitial — stays on the public router so
  // VerticalLayout's domain guard never mounts during the cross-domain hop.
  {
    path: "/onboarding/complete",
    component: OnboardingComplete,
    exact: true,
  },
  { path: "/login", component: Login },
  { path: "/register", component: Register },
  { path: "/forgot-password", component: ForgetPassword },
  { path: "/seller-session", component: SellerSessionHandoff, exact: true },
  { path: "/editor", component: ShopifyAppEditor },
  { path: "/email-verification", component: EmailVerification },
  {
    path: "/public-store/*",
    component: NewStore,
  },
  // Public client-facing quote — accessible by anyone with the URL
  { path: "/q/:id", component: ClientQuote, exact: true },
  { path: "/q/:id/changes", component: ClientQuoteChanges, exact: true },
  // POS.6 — register shell is public; device token / pairing gates inside PosApp
  {
    path: "/pos",
    component: PosApp,
    exact: true,
    standalone: true,
  },
  {
    path: "/pos/pair",
    component: PosApp,
    exact: true,
    standalone: true,
  },
  // POS.1 — public tokenized artwork upload (no login)
  {
    path: "/pos/art-upload/:token",
    component: PosArtUploadPublic,
    exact: true,
  },
  {
    path: "/pos/art-review/:token",
    component: PosArtReviewPublic,
    exact: true,
  },
  {
    path: "/pos/pay/:token",
    component: PosPayPublic,
    exact: true,
  },
  ...(process.env.NODE_ENV === "development"
    ? [
        {
          path: "/dev/embroidery-complete",
          component: EmbroideryCompletePreview,
          exact: true,
        },
      ]
    : []),
  { path: "/privacy", component: PrivacyPage, exact: true },
  {
    path: "/ecommerce-app/privacy",
    component: ShopifyPrivacyPage,
    exact: true,
  },
  {
    path: "/shopify-app/privacy",
    component: EcommerceAppPrivacyRedirect,
    exact: true,
  },
  { path: "/terms", component: TermsPage, exact: true },
  { path: "/billing-info", component: BillingInfoPage, exact: true },
];

export { authProtectedRoutes, publicRoutes };
